import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Drawer, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useUserRole } from '../../contexts/UserRoleContext';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

/**
 * Declarabiliteit — per medewerker per maand: geschreven uren (Gripp), intern/extern, en welk
 * deel van de externe uren binnen het budget van de opdracht viel. Declarabiliteit = uren binnen
 * budget ÷ beschikbare uren (contracturen min feestdagen en vrije dagen); norm 65%.
 *
 * Rekenregels: docs/declarabiliteit-plan.md. Budget per opdracht per soort; overschrijding naar
 * rato over wie er die maand op schreef. Een percentage met "≥" is een ondergrens: er zijn uren
 * op opdrachten waarvan het budget onbekend is.
 */
export const API = `${BACKEND_URL}/Api/declarabiliteit`;
export const ACCENT = { bg: '#e0e7ff', color: '#3730a3' };
export const NAV_FONT = '"Manrope", system-ui, sans-serif';
export const NORM = 65;
export const ROOD = '#b91c1c';
const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const REDEN_LABEL = {
  'boven budget': 'boven budget', kostenregel: 'op kostenregel', offertefase: 'offertefase', 'budget onbekend': 'budget onbekend',
};

export const maandLabel = (m) => (m ? `${MAAND_KORT[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}` : '');
export const uren = (n) => (n === null || n === undefined ? '—' : `${Math.round(n)}u`);
const euro = (n) => (n ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '—');
export const pct = (n) => (n === null || n === undefined ? '—' : `${Math.round(n)}%`);
/** Boven budget als deel van de externe uren. */
export const bovenPct = (c) => (c && c.extern ? (100 * (c.boven || 0)) / c.extern : null);

export function kleurVoor(p) {
  if (p === null || p === undefined) return { bg: '#f1f5f9', fg: '#94a3b8' };
  if (p >= NORM) return { bg: '#dcfce7', fg: '#166534' };
  if (p >= NORM - 10) return { bg: '#fef3c7', fg: '#92400e' };
  return { bg: '#fee2e2', fg: '#991b1b' };
}

export async function haal(url, opties) {
  const r = await authFetch(url, opties);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
  return d;
}

function standaardVenster() {
  const nu = new Date();
  const m = String(nu.getMonth() + 1).padStart(2, '0');
  return { van: `${nu.getFullYear()}-01`, tot: `${nu.getFullYear()}-${m}` };
}

/** Tabelstijl van de matrices (personen en functiegroepen): kolom 1 plakt links, kop plakt boven. */
const matrixSx = (aantalMaanden) => ({
  borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 220 + aantalMaanden * 86,
  '& th, & td': { px: 0.75, py: 0.5, borderBottom: '1px solid #f1f5f9' },
  '& th': { fontFamily: NAV_FONT, fontSize: 12, fontWeight: 700, color: '#475569', position: 'sticky', top: 0, bgcolor: '#f8fafc', zIndex: 1 },
  '& td:first-of-type, & th:first-of-type': { position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 2, textAlign: 'left', minWidth: 200 },
  '& th:first-of-type': { bgcolor: '#f8fafc', zIndex: 3 },
});

/** Eén cel van de matrix: het percentage groot, de uren klein eronder. Zonder onClick
 *  (totalen, bureau) is het een gewone cel — geen knop die niets doet. */
export function Cel({ c, onClick }) {
  if (!c) return <Box sx={{ color: '#cbd5e1', textAlign: 'center', fontSize: 12 }}>·</Box>;
  // Geen uren in Gripp is iets anders dan 0% declarabel: grijs, zodat het rood blijft voor
  // wie wél schreef maar buiten budget of intern (Joris' screenshot, 23 sep).
  const geenUren = !c.geschreven;
  const k = geenUren ? { bg: '#f8fafc', fg: '#94a3b8' } : kleurVoor(c.declarabiliteit);
  const titel = [
    `Geschreven ${uren(c.geschreven)} (intern ${uren(c.intern)}, extern ${uren(c.extern)})`,
    `Binnen budget ${uren(c.binnen)} · boven budget ${uren(c.boven)}${c.overig_niet ? ` · overig niet declarabel ${uren(c.overig_niet)}` : ''}${c.onbekend ? ` · budget onbekend ${uren(c.onbekend)}` : ''}`,
    `Beschikbaar ${uren(c.beschikbaar)}`,
  ].join('\n');
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 12 }}>{titel}</Box>} arrow>
      <Box component={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick}
        sx={{
          all: 'unset', cursor: onClick ? 'pointer' : 'default', display: 'block', width: '100%', boxSizing: 'border-box',
          textAlign: 'center', borderRadius: 1, py: 0.5, bgcolor: k.bg,
          ...(onClick && { '&:hover': { outline: `2px solid ${k.fg}` }, '&:focus-visible': { outline: `2px solid ${ACCENT.color}` } }),
        }}>
        <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 14, color: k.fg, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
          {geenUren ? '—' : `${c.ondergrens && c.declarabiliteit !== null ? '≥' : ''}${pct(c.declarabiliteit)}`}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
          {geenUren ? 'geen uren' : `${uren(c.geschreven)} · ${pct(c.intern_pct)} int`}
        </Typography>
        {/* Boven budget meteen zichtbaar (Joris 23 sep), niet pas in de tooltip. */}
        {!geenUren && (c.boven || 0) >= 0.5 && (
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: ROOD, fontVariantNumeric: 'tabular-nums' }}>
            ▲ {uren(c.boven)} boven
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** Budget van één soort aanpassen (vervangt het Gripp-budget van die soort). */
/** {groep: [soorten]} ↔ bewerkbare lijst. */
export const naarLijst = (groepen) => Object.entries(groepen || {}).map(([naam, soorten]) => ({ naam, soorten: [...soorten] }));
/** Foutmelding voor een groepenlijst, of null (Codex-review 21 okt #3: dubbele namen gingen stil verloren). */
export function groepenFout(lijst) {
  const namen = (lijst || []).filter((g) => g.soorten.length).map((g) => g.naam.trim());
  if (namen.some((n) => !n)) return 'Geef elke groep met soorten een naam.';
  if (namen.some((n) => n.length > 40)) return 'Een groepsnaam is hooguit 40 tekens.';
  const laag = namen.map((n) => n.toLowerCase());
  if (new Set(laag).size !== laag.length) return 'Twee groepen hebben dezelfde naam.';
  return null;
}
export const naarObject = (lijst) => Object.fromEntries((lijst || [])
  .filter((g) => g.naam.trim() && g.soorten.length).map((g) => [g.naam.trim(), g.soorten]));

/**
 * Soortgroepen (Joris, 21 okt): verwante soorten delen binnen een opdracht één budget.
 * Standaard Post (edit, kleur, geluid, ondertiteling) en Productie (camera, regie).
 */
export function SoortgroepenDialoog({ open, magBewerken, onClose, onOpgeslagen }) {
  const [info, setInfo] = useState(null);
  const [lijst, setLijst] = useState([]);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  useEffect(() => {
    if (!open) return;
    setFout(null);
    haal(`${API}/soortgroepen`).then((d) => { setInfo(d); setLijst(naarLijst(d.groepen)); }).catch((e) => setFout(e.message));
  }, [open]);
  const gebruikt = (behalve) => new Set(lijst.filter((_, i) => i !== behalve).flatMap((g) => g.soorten));
  const zet = (i, veld, waarde) => setLijst((l) => l.map((g, j) => (j === i ? { ...g, [veld]: waarde } : g)));

  const bewaar = async (groepen) => {
    await haal(`${API}/soortgroepen`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ groepen }) });
  };
  const opslaan = async () => {
    const f = groepenFout(lijst);
    if (f) { setFout(f); return; }
    setBezig(true);
    try {
      const vorige = info?.groepen || {};
      await bewaar(naarObject(lijst));
      biedOngedaanAan('Soortgroepen aangepast', async () => { await bewaar(vorige); onOpgeslagen(); });
      onOpgeslagen();
      onClose();
    } catch (e) { setFout(e.message); } finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Soortgroepen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Soorten in dezelfde groep delen binnen een opdracht één budget: uren op de ene soort mogen het budget van de
          andere gebruiken. Soorten zonder groep hebben elk hun eigen budget. Kostensoorten hebben geen urenbudget.
        </Typography>
        {fout && <Alert severity="warning" sx={{ mb: 2 }}>{fout}</Alert>}
        {!info && !fout && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
        {info && lijst.map((g, i) => (
          // eslint-disable-next-line react/no-array-index-key -- de naam is bewerkbaar en dus geen stabiele sleutel
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
            <TextField size="small" label="Groep" value={g.naam} disabled={!magBewerken} sx={{ width: 140 }}
              onChange={(e) => zet(i, 'naam', e.target.value)} />
            <Autocomplete multiple size="small" sx={{ flex: 1 }} disabled={!magBewerken}
              options={(info.soorten || []).filter((x) => !gebruikt(i).has(x))} value={g.soorten}
              onChange={(_, v) => zet(i, 'soorten', v)}
              renderInput={(params) => <TextField {...params} label="Soorten" />} />
            {magBewerken && (
              <IconButton aria-label={`Groep ${g.naam || i + 1} verwijderen`} onClick={() => setLijst((l) => l.filter((_, j) => j !== i))}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        {info && magBewerken && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setLijst((l) => [...l, { naam: '', soorten: [] }])}>Groep toevoegen</Button>
            <Button size="small" onClick={() => setLijst(naarLijst(info.standaard))}>Standaard terugzetten</Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{magBewerken ? 'Annuleren' : 'Sluiten'}</Button>
        {magBewerken && <Button variant="contained" onClick={opslaan} disabled={bezig || !info}>Opslaan</Button>}
      </DialogActions>
    </Dialog>
  );
}

function BudgetDialoog({ open, soort, project, huidig, onClose, onOpgeslagen }) {
  const [urenVeld, setUrenVeld] = useState('');
  const [euroVeld, setEuroVeld] = useState('');
  const [notitie, setNotitie] = useState('');
  const [bezig, setBezig] = useState(false);
  useEffect(() => {
    if (!open) return;
    setUrenVeld(huidig?.uren ?? '');
    setEuroVeld(huidig?.euro ?? '');
    setNotitie(huidig?.notitie ?? '');
  }, [open, huidig]);

  const opslaan = async () => {
    setBezig(true);
    try {
      const vorige = huidig ? { uren: huidig.uren, euro: huidig.euro, notitie: huidig.notitie } : null;
      const body = { project_nummer: project, soort, uren: urenVeld === '' ? null : urenVeld, euro: euroVeld === '' ? null : euroVeld, notitie };
      await haal(`${API}/overrides`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      biedOngedaanAan(`Budget ${soort} aangepast`, async () => {
        if (vorige) {
          await haal(`${API}/overrides`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ project_nummer: project, soort, ...vorige }) });
        } else {
          const lijst = await haal(`${API}/overrides?project_nummer=${encodeURIComponent(project)}`);
          const nieuw = lijst.find((o) => o.soort === soort);
          if (nieuw) await haal(`${API}/overrides/${nieuw.id}`, { method: 'DELETE' });
        }
        onOpgeslagen();
      });
      onOpgeslagen();
      onClose();
    } catch (e) {
      toast.error(`Opslaan mislukt: ${e.message}`);
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: NAV_FONT, fontWeight: 800 }}>Budget {soort}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          Vervangt het budget uit de Gripp-offerteregels voor deze soort in opdracht {project}. Leeg laten = Gripp aanhouden, 0 = bewust geen budget.
        </Typography>
        <TextField label="Uren" type="number" size="small" value={urenVeld} onChange={(e) => setUrenVeld(e.target.value)} />
        <TextField label="Euro (verbruikt tegen het tarief van wie schrijft)" type="number" size="small" value={euroVeld} onChange={(e) => setEuroVeld(e.target.value)} />
        <TextField label="Toelichting" size="small" multiline minRows={2} value={notitie} onChange={(e) => setNotitie(e.target.value)}
          placeholder="bv. retainer loopt heel 2026, 12 × 20 uur" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button variant="contained" onClick={opslaan} disabled={bezig}>Opslaan</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Uren met decimalen voor losse boekingen en soorttotalen (1,5u blijft 1,5u). */
const urenFijn = (n) => (n === null || n === undefined ? '—' : `${Number(n).toLocaleString('nl-NL', { maximumFractionDigits: 2 })}u`);
const urenEen = (n) => urenFijn(n === null || n === undefined ? n : Math.round(n * 10) / 10);
const budgetTekst = (u, e) => [u ? urenEen(u) : null, e ? euro(e) : null].filter(Boolean).join(' + ') || '0u';

/**
 * Budget telt per opdracht per soort (Joris, 21 okt): 3× Montage is één Montage-budget, dus uren op één
 * regel terwijl de andere leeg blijven zijn géén overschrijding. Hier staat het soorttotaal; de losse
 * offerteregels zijn alleen uitleg.
 */
export function soortSamenvatting(soort, s) {
  const n = s?.aantal_regels || 0;
  return `${soort}: ${urenEen(s?.geschreven || 0)} geschreven van ${budgetTekst(s?.budget_uren, s?.budget_euro)} begroot`
    + (n ? ` (${n} ${n === 1 ? 'regel' : 'regels'})` : '');
}

/** Soortgroep (Joris, 21 okt): verwante soorten delen één budget, bv. "Post (Editing & montage, Kleur & VFX)". */
export function groepSamenvatting(groep, g) {
  if (!groep || !g) return null;
  return soortSamenvatting(`${groep} (${(g.soorten || []).join(', ')})`, g);
}

/** Uitklapbare lijst van de offerteregels achter één soortbudget. */
export function SoortRegels({ regels, zonderRegel }) {
  const [open, setOpen] = useState(false);
  if (!(regels || []).length && !zonderRegel) return null;
  return (
    <Box component="span">
      {' · '}
      <Box component="button" type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        sx={{ all: 'unset', cursor: 'pointer', color: ACCENT.color, '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: `2px solid ${ACCENT.color}` } }}>
        {open ? 'regels verbergen' : 'regels tonen'}
      </Box>
      {open && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, fontSize: 12, color: '#475569' }}>
          {(regels || []).map((x, i) => (
            <li key={x.id ?? i}>{x.naam} · {budgetTekst(x.budget_uren, x.budget_euro)} begroot · {urenEen(x.geschreven)} geschreven</li>
          ))}
          {zonderRegel ? <li>zonder bekende regel (op naam herkend) · {urenEen(zonderRegel)} geschreven</li> : null}
        </Box>
      )}
    </Box>
  );
}

/** Opdracht: uren versus budget per soort, met wie er per maand schreef. */
function OpdrachtPaneel({ nummer, magBewerken, onTerug }) {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [fout, setFout] = useState(null);
  const [dialoog, setDialoog] = useState(null);
  const laad = useCallback(async () => {
    setFout(null);
    try {
      const [p, o] = await Promise.all([
        haal(`${API}/project/${encodeURIComponent(nummer)}`),
        haal(`${API}/overrides?project_nummer=${encodeURIComponent(nummer)}`),
      ]);
      setData(p);
      setOverrides(o);
    } catch (e) { setFout(e.message); }
  }, [nummer]);
  useEffect(() => { laad(); }, [laad]);

  if (fout) return <Alert severity="warning">{fout}</Alert>;
  if (!data) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  const opGeschreven = (a, b) => (b[1].geschreven || 0) - (a[1].geschreven || 0);
  const groepen = Object.entries(data.groepen || {}).sort(opGeschreven);
  // Eerst de soortgroepen met hun soorten eronder, dan de losse soorten.
  const soorten = [
    ...groepen.flatMap(([g, gc]) => [['__groep__', { ...gc, groepNaam: g }],
      ...Object.entries(data.soorten || {}).filter(([, s]) => s.groep === g).sort(opGeschreven)]),
    ...Object.entries(data.soorten || {}).filter(([, s]) => !s.groep || !(data.groepen || {})[s.groep]).sort(opGeschreven),
  ];
  return (
    <Box>
      {onTerug && <Button size="small" onClick={onTerug} sx={{ mb: 1, textTransform: 'none' }}>← terug naar de maand</Button>}
      <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 18 }}>{data.nummer} · {data.naam}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {data.klant || '—'} · eerste uur {data.eerste_uur}
        {!data.heeft_regels && ' · geen offerteregels in de spiegel: budget onbekend'}
        {data.historie_onvolledig && ' · historie vóór de eerste gescande datum ontbreekt'}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums',
          '& th': { textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', py: 0.75, pr: 1.5, whiteSpace: 'nowrap' },
          '& td': { borderBottom: '1px solid #f1f5f9', py: 0.75, pr: 1.5, verticalAlign: 'top' } }}>
          <thead><tr><th>Soort</th><th>Budget</th><th>Geschreven</th><th>Binnen</th><th>Boven</th><th /></tr></thead>
          <tbody>
            {soorten.map(([soort, s]) => {
              if (soort === '__groep__') {
                return (
                  <tr key={`groep-${s.groepNaam}`} style={{ background: '#f8fafc' }}>
                    <td>
                      <Box component="span" sx={{ fontWeight: 700 }}>{s.groepNaam}</Box>
                      <Typography component="div" variant="caption" color="text.secondary">
                        één budget voor {(s.soorten || []).join(', ')}
                      </Typography>
                    </td>
                    <td style={{ fontWeight: 700 }}>{budgetTekst(s.budget_uren, s.budget_euro)}</td>
                    <td style={{ fontWeight: 700 }}>{uren(s.geschreven)}</td>
                    <td style={{ fontWeight: 700 }}>{uren(s.binnen)}</td>
                    <td style={{ fontWeight: 700, color: s.boven > 0 ? '#991b1b' : undefined }}>{uren(s.boven)}</td>
                    <td />
                  </tr>
                );
              }
              const o = overrides.find((x) => x.soort === soort);
              return (
                <tr key={soort}>
                  <td style={s.groep && (data.groepen || {})[s.groep] ? { paddingLeft: 16 } : undefined}>
                    {soort}
                    {s.kosten && <Chip label="kosten" size="small" sx={{ ml: 0.75, height: 18, fontSize: 10 }} />}
                    {s.onzeker && <Chip label="soort onzeker" size="small" sx={{ ml: 0.75, height: 18, fontSize: 10 }} />}
                    {o && <Tooltip title={o.notitie || ''}><Chip label="aangepast" size="small" color="primary" variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10 }} /></Tooltip>}
                    {(s.aantal_regels > 0 || s.geschreven_zonder_regel) ? (
                      <Typography component="div" variant="caption" color="text.secondary">
                        {s.aantal_regels || 0} {s.aantal_regels === 1 ? 'regel' : 'regels'}
                        <SoortRegels regels={s.regels} zonderRegel={s.geschreven_zonder_regel} />
                      </Typography>
                    ) : null}
                  </td>
                  <td>{s.kosten ? '—' : [s.budget_uren ? uren(s.budget_uren) : null, s.budget_euro ? euro(s.budget_euro) : null].filter(Boolean).join(' + ') || '0'}</td>
                  <td>{uren(s.geschreven)}</td>
                  <td>{uren(s.binnen)}</td>
                  <td style={{ color: s.boven > 0 ? '#991b1b' : undefined }}>
                    {s.kosten ? (s.niet_declarabel ? `${uren(s.niet_declarabel)} niet declarabel` : '—') : uren(s.boven)}
                  </td>
                  <td>{magBewerken && !s.kosten && (
                    <Button size="small" sx={{ textTransform: 'none', minWidth: 0 }}
                      onClick={() => setDialoog({ soort, huidig: o || null })}>Budget aanpassen</Button>
                  )}</td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 700, fontSize: 13, mt: 2.5, mb: 1 }}>Wie schreef wanneer</Typography>
      {Object.entries(data.schrijvers_per_maand || {}).map(([soort, maanden]) => (
        <Box key={soort} sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>{soort}</Typography>
          {Object.entries(maanden).map(([m, wie]) => (
            <Typography key={m} variant="body2" sx={{ fontSize: 12.5, color: '#334155' }}>
              {maandLabel(m)}: {Object.entries(wie).map(([n, u]) => `${n} ${uren(u)}`).join(', ')}
            </Typography>
          ))}
        </Box>
      ))}
      <BudgetDialoog open={!!dialoog} soort={dialoog?.soort} project={nummer} huidig={dialoog?.huidig}
        onClose={() => setDialoog(null)} onOpgeslagen={laad} />
    </Box>
  );
}

const dagLabel = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const ONBEKEND = 'budget onbekend';
const BOVEN = 'boven budget';
const OVERIG_REDENEN = ['kostenregel', 'offertefase'];

/**
 * Tegels in het detailpaneel (Joris, 21 okt): elke tegel met uren is een filter voor de lijst eronder.
 * `extern` (Declarabiliteit) is de standaardweergave; een tweede klik op een actieve tegel zet hem terug.
 * Boven budget is alleen echte overschrijding; offertefase en kostenregels staan sinds 21 okt in een eigen
 * tegel "Overig niet declarabel" (zelfde indeling als de maandcijfers).
 */
export const STANDAARD_FILTER = 'extern';
export const TEGELS = [
  { key: 'extern', label: 'Declarabiliteit', titel: 'Externe uren per opdracht en soort' },
  { key: 'geschreven', label: 'Geschreven', veld: 'geschreven', titel: 'Alle geschreven uren' },
  { key: 'intern', label: 'Intern', veld: 'intern', titel: 'Interne uren per soort' },
  { key: 'binnen', label: 'Binnen budget', veld: 'binnen', titel: 'Uren binnen budget per opdracht en soort', kort: 'binnen budget' },
  { key: 'boven', label: 'Boven budget', veld: 'boven', titel: 'Uren boven budget per opdracht en soort', kort: 'boven budget' },
  { key: 'overig', label: 'Overig niet declarabel', veld: 'overig_niet', titel: 'Offertefase en kostenregels per opdracht en soort', kort: 'overig niet declarabel' },
  { key: 'onbekend', label: 'Budget onbekend', veld: 'onbekend', titel: 'Uren zonder bekend budget per opdracht en soort', kort: 'budget onbekend' },
  { key: 'beschikbaar', label: 'Beschikbaar', veld: 'beschikbaar', titel: 'Zo zijn de beschikbare uren opgebouwd' },
];

/** Uren van één externe regel die in de gekozen categorie vallen. */
export function regelUren(r, filter) {
  const redenen = r.redenen || {};
  if (filter === 'binnen') return r.binnen || 0;
  if (filter === 'onbekend') return redenen[ONBEKEND] || 0;
  if (filter === 'boven') return redenen[BOVEN] || 0;
  if (filter === 'overig') return OVERIG_REDENEN.reduce((t, k) => t + (redenen[k] || 0), 0);
  return r.geschreven || 0;
}

/** Externe regels voor een categorie-filter: alleen regels met uren in die categorie, grootste eerst. */
export function filterRegels(regels, filter) {
  if (!['binnen', 'boven', 'overig', 'onbekend'].includes(filter)) return regels || [];
  return (regels || []).filter((r) => Math.abs(regelUren(r, filter)) > 0.005)
    .sort((a, b) => regelUren(b, filter) - regelUren(a, filter));
}

/** Klikbaar = er zijn uren in die tegel (of, bij Beschikbaar, een opbouw om te laten zien). */
export function tegelKlikbaar(tegel, cel, data) {
  if (tegel.key === STANDAARD_FILTER) return true;
  if (tegel.key === 'beschikbaar') return !!data?.beschikbaar_opbouw;
  return Math.abs(cel?.[tegel.veld] || 0) > 0.005;
}

function Tegel({ tegel, waarde, actief, klikbaar, onKies }) {
  const inhoud = (
    <>
      <Typography sx={{ fontSize: 10.5, color: actief ? ACCENT.color : '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tegel.label}</Typography>
      <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums', color: actief ? ACCENT.color : '#0f172a' }}>{waarde}</Typography>
    </>
  );
  const basis = { px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: actief ? ACCENT.bg : '#f8fafc', border: '1px solid', borderColor: actief ? ACCENT.color : '#e2e8f0' };
  if (!klikbaar) return <Box sx={basis}>{inhoud}</Box>;
  return (
    <Box component="button" type="button" aria-pressed={actief} onClick={() => onKies(tegel.key)}
      sx={{
        ...basis, font: 'inherit', textAlign: 'left', cursor: 'pointer',
        '&:hover': { borderColor: actief ? ACCENT.color : '#94a3b8' },
        '&:focus-visible': { outline: `2px solid ${ACCENT.color}`, outlineOffset: 2 },
      }}>
      {inhoud}
    </Box>
  );
}

function ExternRegel({ r, filter, onOpdracht }) {
  const tegel = TEGELS.find((t) => t.key === filter);
  const categorie = tegel?.kort ? regelUren(r, filter) : null;
  const overigeRedenen = Object.entries(r.redenen || {})
    .filter(([k]) => (filter === 'overig' ? OVERIG_REDENEN.includes(k) : true));
  return (
    <Box sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Box component="button" type="button" onClick={() => onOpdracht(r.project)}
          sx={{ all: 'unset', cursor: 'pointer', fontWeight: 700, color: ACCENT.color, '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: `2px solid ${ACCENT.color}` } }}>
          {r.project} · {r.naam || 'onbekende opdracht'}
        </Box>
        <Typography variant="body2" color="text.secondary">{r.soort}</Typography>
      </Box>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {categorie !== null ? (
          <>
            <Box component="span" sx={{ fontWeight: 700 }}>{urenFijn(Math.round(categorie * 10) / 10)} {tegel.kort}</Box>
            {` · van ${uren(r.geschreven)} geschreven`}
            {filter === 'overig' && overigeRedenen.map(([k, v]) => ` · waarvan ${uren(v)} ${REDEN_LABEL[k] || k}`).join('')}
          </>
        ) : (
          <>
            {uren(r.geschreven)} geschreven · {uren(r.binnen)} binnen budget
            {overigeRedenen.map(([k, v]) => ` · ${uren(v)} ${REDEN_LABEL[k] || k}`).join('')}
          </>
        )}
      </Typography>
      {r.groep && r.groep_cijfers ? (
        <Typography component="div" variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>
          {groepSamenvatting(r.groep, r.groep_cijfers)}, één budget
        </Typography>
      ) : null}
      {(r.budget_uren || r.budget_euro || r.aantal_regels) ? (
        <Typography variant="caption" color="text.secondary">
          {soortSamenvatting(r.soort, { geschreven: r.soort_geschreven_totaal, budget_uren: r.budget_uren,
            budget_euro: r.budget_euro, aantal_regels: r.aantal_regels })}, door iedereen
          <SoortRegels regels={r.soort_regels} zonderRegel={r.geschreven_zonder_regel} />
        </Typography>
      ) : null}
    </Box>
  );
}

function ExternLijst({ regels, filter, onOpdracht }) {
  const lijst = filterRegels(regels, filter);
  if (lijst.length === 0) return <Typography variant="body2" color="text.secondary">Geen externe uren{filter === STANDAARD_FILTER || filter === 'geschreven' ? '' : ' in deze categorie'} in deze maand.</Typography>;
  return lijst.map((r) => <ExternRegel key={`${r.project}-${r.soort}`} r={r} filter={filter} onOpdracht={onOpdracht} />);
}

/** Eén soort intern werk; uitklappen toont de losse boekingen. */
function InternSoort({ s }) {
  const [open, setOpen] = useState(false);
  const meerProjecten = (s.projecten || []).length > 1;
  return (
    <Box sx={{ borderBottom: '1px solid #f1f5f9' }}>
      <Box component="button" type="button" aria-expanded={open} onClick={() => setOpen((x) => !x)}
        sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 1, py: 1,
          '&:focus-visible': { outline: `2px solid ${ACCENT.color}` } }}>
        <ExpandMoreIcon fontSize="small" sx={{ color: '#94a3b8', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
        <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 14 }}>{s.soort}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {s.boekingen.length} {s.boekingen.length === 1 ? 'boeking' : 'boekingen'}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 14, minWidth: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{urenFijn(s.uren)}</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, mb: 1, ml: 3.5, maxWidth: 'calc(100% - 28px)',
          '& td': { py: 0.4, pr: 1.5, verticalAlign: 'top', borderTop: '1px solid #f8fafc' } }}>
          <tbody>
            {s.boekingen.map((b, i) => (
              <tr key={`${b.datum}-${i}`}>
                <td style={{ whiteSpace: 'nowrap', color: '#475569', width: 90 }}>{dagLabel(b.datum)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right', width: 48, fontVariantNumeric: 'tabular-nums' }}>{urenFijn(b.uren)}</td>
                <td>
                  {b.omschrijving || <Box component="span" sx={{ color: '#94a3b8' }}>geen omschrijving</Box>}
                  {meerProjecten && b.project && <Box component="span" sx={{ color: '#94a3b8' }}> · {b.project}</Box>}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Collapse>
    </Box>
  );
}

function InternLijst({ intern }) {
  if (!intern?.length) return <Typography variant="body2" color="text.secondary">Geen interne uren in deze maand.</Typography>;
  return intern.map((s) => <InternSoort key={s.soort} s={s} />);
}

function OpbouwBeschikbaar({ o }) {
  if (!o) return <Typography variant="body2" color="text.secondary">Geen contracturen bekend — vul ze aan in Gebruikersbeheer.</Typography>;
  const rij = (label, waarde, toelichting, sterk) => (
    <tr>
      <td style={{ fontWeight: sterk ? 700 : 400 }}>{label}{toelichting && <Box component="div" sx={{ color: '#94a3b8', fontSize: 12 }}>{toelichting}</Box>}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: sterk ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>{waarde}</td>
    </tr>
  );
  const dagMetUren = (v) => (v.uren < o.dagdoel ? `${dagLabel(v.datum)} (${urenFijn(v.uren)})` : dagLabel(v.datum));
  const aangevraagd = o.aangevraagd_niet_geteld || [];
  const vrijRij = (label, bron) => {
    const dagen = (o.vrije_dagen || []).filter((v) => (v.bron || 'airtable') === bron);
    const uren = dagen.reduce((som, v) => som + v.uren, 0);
    if (!dagen.length && bron === 'gripp') return null;
    return rij(`${label} (${dagen.length} ${dagen.length === 1 ? 'dag' : 'dagen'})`, uren ? `−${urenFijn(uren)}` : '—',
      dagen.map(dagMetUren).join(', '));
  };
  return (
    <Box>
      <Box component="table" sx={{ width: '100%', maxWidth: 520, borderCollapse: 'collapse', fontSize: 13.5,
        '& td': { py: 0.75, pr: 1.5, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' } }}>
        <tbody>
          {rij(`Contract ${urenFijn(o.contracturen)} per week over ${o.roosterdagen_per_week} roosterdagen`, `${urenFijn(o.dagdoel)} per dag`)}
          {rij(`${o.roosterdagen} roosterdagen`, urenFijn(o.bruto), `${dagLabel(o.van)} t/m ${dagLabel(o.tot)}`)}
          {rij(`− feestdagen (${o.feestdagen.length})`, o.feestdag_uren ? `−${urenFijn(o.feestdag_uren)}` : '—', o.feestdagen.map(dagLabel).join(', '))}
          {vrijRij('− verlof volgens Gripp', 'gripp')}
          {vrijRij('− vrij, verlof en ziek volgens de planning', 'airtable')}
          {rij('Beschikbaar', urenFijn(o.netto), null, true)}
        </tbody>
      </Box>
      {aangevraagd.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aangevraagd in Gripp, nog niet goedgekeurd (telt niet mee als Gripp-verlof): {aangevraagd.map((v) => dagMetUren(v)).join(', ')}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        De lopende maand telt t/m gisteren. Contracturen komen uit Gebruikersbeheer. Verlof komt eerst uit Gripp
        ({o.verlof_keuze === 'ook_aangevraagd' ? 'goedgekeurd én aangevraagd' : 'alleen goedgekeurd'}); de vrije-dag-taken
        in de planning tellen alleen op dagen waarvoor Gripp niets heeft. Een dag telt nooit dubbel.
      </Typography>
    </Box>
  );
}

/** Zijpaneel: wat zit er achter één cel. */
export function CelPaneel({ keuze, magBewerken, onClose }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [opdracht, setOpdracht] = useState(null);
  const [filter, setFilter] = useState(STANDAARD_FILTER);
  useEffect(() => {
    if (!keuze) return;
    setData(null); setFout(null); setOpdracht(null); setFilter(STANDAARD_FILTER);
    haal(`${API}/persoon/${encodeURIComponent(keuze.email)}?maand=${keuze.maand}`).then(setData).catch((e) => setFout(e.message));
  }, [keuze]);

  // Tweede klik op de actieve tegel zet het filter terug op de standaardweergave.
  const kies = (key) => setFilter((f) => (f === key ? STANDAARD_FILTER : key));
  const cel = data?.cel;
  const actieveTegel = TEGELS.find((t) => t.key === filter) || TEGELS[0];
  const waardeVan = (t) => (t.key === STANDAARD_FILTER ? `${cel.ondergrens ? '≥' : ''}${pct(cel.declarabiliteit)}` : uren(cel[t.veld]));
  const externTotaal = (data?.regels || []).reduce((s, r) => s + (r.geschreven || 0), 0);
  const internTotaal = (data?.intern || []).reduce((s, x) => s + (x.uren || 0), 0);
  const kop = (tekst, mt = 0) => <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 700, fontSize: 13, mb: 1, mt }}>{tekst}</Typography>;

  return (
    <Drawer anchor="right" open={!!keuze} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 720 }, p: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 20 }}>{keuze?.naam}</Typography>
          <Typography variant="body2" color="text.secondary">
            {maandLabel(keuze?.maand)}
            {keuze?.email && (
              <>
                {' · '}
                <Box component={RouterLink} to={`/declarabiliteit/persoon/${encodeURIComponent(keuze.email)}`}
                  sx={{ color: ACCENT.color, fontWeight: 700 }}>
                  hele jaar en vergelijking
                </Box>
              </>
            )}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Sluiten"><CloseIcon /></IconButton>
      </Box>
      {opdracht ? (
        <OpdrachtPaneel nummer={opdracht} magBewerken={magBewerken} onTerug={() => setOpdracht(null)} />
      ) : fout ? <Alert severity="warning">{fout}</Alert> : !data ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : (
        <>
          {cel && (
            <Box role="group" aria-label="Filter de lijst" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {TEGELS.map((t) => (
                <Tegel key={t.key} tegel={t} waarde={waardeVan(t)} actief={filter === t.key}
                  klikbaar={tegelKlikbaar(t, cel, data)} onKies={kies} />
              ))}
            </Box>
          )}
          {kop(cel ? actieveTegel.titel : TEGELS[0].titel)}
          {(!cel || ['extern', 'binnen', 'boven', 'overig', 'onbekend'].includes(filter)) && (
            <ExternLijst regels={data.regels} filter={cel ? filter : STANDAARD_FILTER} onOpdracht={setOpdracht} />
          )}
          {cel && filter === 'intern' && <InternLijst intern={data.intern} />}
          {cel && filter === 'geschreven' && (
            <>
              {kop(`Extern · ${urenFijn(Math.round(externTotaal * 10) / 10)}`, 1)}
              <ExternLijst regels={data.regels} filter="geschreven" onOpdracht={setOpdracht} />
              {kop(`Intern · ${urenFijn(Math.round(internTotaal * 10) / 10)}`, 2.5)}
              <InternLijst intern={data.intern} />
            </>
          )}
          {cel && filter === 'beschikbaar' && <OpbouwBeschikbaar o={data.beschikbaar_opbouw} />}
        </>
      )}
    </Drawer>
  );
}

export default function Declarabiliteit() {
  const { permissions } = useUserRole();
  const magBewerken = !!permissions?.kpi_dashboard?.edit;
  const [venster, setVenster] = useState(standaardVenster);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [keuze, setKeuze] = useState(null);
  const [toonDetails, setToonDetails] = useState(false);
  const [groepFilter, setGroepFilter] = useState('');
  const [groepenOpen, setGroepenOpen] = useState(false);

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laad = useCallback(async () => {
    setLaden(true); setFout(null);
    try {
      const [m, s] = await Promise.all([
        haal(`${API}/maanden?van=${venster.van}&tot=${venster.tot}`),
        haal(`${API}/status`),
      ]);
      setData(m); setStatus(s);
    } catch (e) { setFout(e.message); } finally { setLaden(false); }
  }, [venster]);
  useEffect(() => { laad(); }, [laad]);

  // Loopt er een sync, dan elke 10 s de status verversen tot hij klaar is.
  useEffect(() => {
    if (!status?.sync_loopt) return undefined;
    const t = setInterval(async () => {
      try {
        const s = await haal(`${API}/status`);
        setStatus(s);
        if (!s.sync_loopt) { toast.success('Uren uit Gripp bijgewerkt'); laad(); }
      } catch { /* volgende poging */ }
    }, 10000);
    return () => clearInterval(t);
  }, [status?.sync_loopt, laad]);

  const startSync = async () => {
    try {
      const r = await haal(`${API}/sync`, { method: 'POST' });
      if (r.gestart) {
        toast.success('Uren ophalen gestart — de hele historie duurt een paar minuten');
        setStatus((s) => ({ ...(s || {}), sync_loopt: true }));
      } else {
        toast(r.reden || 'Er loopt al een urensync');
      }
    } catch (e) { toast.error(`Starten mislukt: ${e.message}`); }
  };

  const exporteer = async () => {
    try {
      const r = await authFetch(`${API}/export.csv?van=${venster.van}&tot=${venster.tot}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `declarabiliteit_${venster.van}_${venster.tot}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(`Export mislukt: ${e.message}`); }
  };

  const leeg = status && !status.spiegel?.n;
  // Tussentijds vastgelegde uren zonder afgeronde volledige ronde: cijfers kloppen nog niet.
  const onvolledig = !!status?.spiegel?.n && !status?.dekking?.volledig_gescand_op;
  const bureau = data?.totaal;
  const spiegelTot = status?.spiegel?.tot;
  const maanden = useMemo(() => data?.maanden || [], [data]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="percent"
        title="Declarabiliteit"
        subtitle={`Uren binnen budget ÷ beschikbare uren, per medewerker per maand · norm ${NORM}%`}
        accent={ACCENT}
        rightActions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField size="small" type="month" label="Van" value={venster.van}
              onChange={(e) => e.target.value && setVenster((v) => ({ ...v, van: e.target.value }))} sx={{ width: 150 }} />
            <TextField size="small" type="month" label="T/m" value={venster.tot}
              onChange={(e) => e.target.value && setVenster((v) => ({ ...v, tot: e.target.value }))} sx={{ width: 150 }} />
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exporteer} disabled={!data}>CSV</Button>
            <Tooltip title="Welke soorten binnen een opdracht één budget delen">
              <Button size="small" variant="outlined" onClick={() => setGroepenOpen(true)}>Soortgroepen</Button>
            </Tooltip>
            {magBewerken && (
              <Tooltip title="Haalt de urenregels opnieuw uit Gripp (alleen lezen). Gebeurt ook elke nacht.">
                <span>
                  <Button size="small" variant="outlined" onClick={startSync} disabled={!!status?.sync_loopt}
                    startIcon={status?.sync_loopt ? <CircularProgress size={14} /> : <SyncIcon />}>
                    {status?.sync_loopt
                      ? `Bezig…${status?.voortgang?.regels ? ` ${status.voortgang.regels.toLocaleString('nl-NL')} regels` : ''}`
                      : 'Uren ophalen'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      />

      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {leeg && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Er zijn nog geen Gripp-uren opgehaald. {magBewerken ? 'Klik op "Uren ophalen" — de eerste keer duurt dat een paar minuten.' : 'Vraag iemand met bewerkrecht op het MT-dashboard om de uren op te halen.'}
        </Alert>
      )}

      {onvolledig && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          De eerste volledige ophaalronde uit Gripp is nog niet afgerond — de cijfers hieronder zijn onvolledig.
          {status?.sync_loopt ? ' Hij loopt nu; deze pagina ververst vanzelf als hij klaar is.' : ' Start "Uren ophalen" opnieuw.'}
        </Alert>
      )}

      {bureau && !leeg && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          {[
            ['Bureau', `${bureau.ondergrens ? '≥' : ''}${pct(bureau.declarabiliteit)}`, kleurVoor(bureau.declarabiliteit)],
            ['Geschreven', uren(bureau.geschreven)],
            ['Intern', pct(bureau.intern_pct)],
            ['Extern binnen budget', pct(bureau.binnen_pct_van_extern)],
            ['Boven budget', `${uren(bureau.boven)} · ${pct(bovenPct(bureau))}`, (bureau.boven || 0) > 0 ? { bg: '#fef2f2', fg: ROOD } : null],
            ['Overig niet declarabel', uren(bureau.overig_niet)],
            ['Beschikbaar', uren(bureau.beschikbaar)],
          ].map(([l, w, k]) => (
            <Box key={l} sx={{ px: 2, py: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: k?.bg || '#fff', minWidth: 140 }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{l}</Typography>
              <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 22, color: k?.fg || '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{w}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {data && !leeg && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Berekend {new Date(data.berekend_op).toLocaleString('nl-NL')} · Gripp-uren t/m {spiegelTot || '—'}
          {' · '}lopende maand t/m gisteren · "≥" = ondergrens (uren op opdrachten zonder bekend budget)
          {bureau?.beschikbaar_onvolledig ? ' · bureau zonder medewerkers zonder contracturen' : ''}
          {(data.waarschuwingen?.length || Object.keys(data.niet_gekoppeld || {}).length) ? (
            <Box component="button" type="button" onClick={() => setToonDetails((x) => !x)}
              sx={{ all: 'unset', cursor: 'pointer', color: '#b45309', fontWeight: 700, ml: 1 }}>
              {toonDetails ? 'verberg aandachtspunten' : `${(data.waarschuwingen?.length || 0) + Object.keys(data.niet_gekoppeld || {}).length} aandachtspunten`}
            </Box>
          ) : null}
        </Typography>
      )}
      <Collapse in={toonDetails}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {(data?.waarschuwingen || []).map((w) => <div key={w}>{w} — beschikbaarheid onbekend (Gebruikersbeheer → contracturen)</div>)}
          {Object.entries(data?.niet_gekoppeld || {}).map(([n, u]) => <div key={n}>Gripp-naam "{n}" niet gekoppeld aan een persoon ({uren(u)}) — telt wel mee in het budgetverbruik</div>)}
        </Alert>
      </Collapse>

      {laden && !data && <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={24} /></Box>}

      {/* Per functiegroep (Joris, 23 sep): samenvatting vóór de mensen. Klik op een groep = filter. */}
      {data && !leeg && data.groepen?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 15, mb: 1 }}>Per functiegroep</Typography>
          <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
            <Box component="table" sx={matrixSx(maanden.length)}>
              <thead>
                <tr>
                  <th>Functiegroep</th>
                  {maanden.map((m) => <th key={m} style={{ textAlign: 'center' }}>{maandLabel(m)}</th>)}
                  <th style={{ textAlign: 'center' }}>Totaal</th>
                </tr>
              </thead>
              <tbody>
                {data.groepen.map((g) => (
                  <tr key={g.groep}>
                    <td>
                      <Tooltip title={g.leden.join(', ')} arrow placement="right">
                        <Box component="button" type="button" onClick={() => setGroepFilter((f) => (f === g.groep ? '' : g.groep))}
                          sx={{ all: 'unset', cursor: 'pointer', display: 'block', '&:focus-visible': { outline: `2px solid ${ACCENT.color}` } }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: groepFilter === g.groep ? ACCENT.color : 'inherit' }}>{g.groep}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{g.leden.length} {g.leden.length === 1 ? 'persoon' : 'personen'}</Typography>
                        </Box>
                      </Tooltip>
                    </td>
                    {maanden.map((m) => <td key={m}><Cel c={g.maanden[m]} /></td>)}
                    <td><Cel c={g.totaal} /></td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </Box>
      )}

      {data && !leeg && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 15, mr: 1 }}>Per medewerker</Typography>
          {['', ...(data.groepen || []).map((g) => g.groep)].map((g) => (
            <Chip key={g || 'iedereen'} label={g || 'Iedereen'} size="small" onClick={() => setGroepFilter(g)}
              variant={groepFilter === g ? 'filled' : 'outlined'} color={groepFilter === g ? 'primary' : 'default'} />
          ))}
        </Box>
      )}

      {data && !leeg && (
        <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
          <Box component="table" sx={matrixSx(maanden.length)}>
            <thead>
              <tr>
                <th>Medewerker</th>
                {maanden.map((m) => <th key={m} style={{ textAlign: 'center' }}>{maandLabel(m)}</th>)}
                <th style={{ textAlign: 'center' }}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              {data.personen.filter((p) => !groepFilter || p.groep === groepFilter).map((p) => (
                <tr key={p.email}>
                  <td>
                    <Typography component={RouterLink} to={`/declarabiliteit/persoon/${encodeURIComponent(p.email)}`}
                      sx={{ fontWeight: 700, fontSize: 13.5, color: 'inherit', textDecoration: 'none', '&:hover': { color: ACCENT.color, textDecoration: 'underline' } }}>
                      {p.naam}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>{p.functie || '—'}</Typography>
                  </td>
                  {maanden.map((m) => (
                    <td key={m}><Cel c={p.maanden[m]} onClick={() => setKeuze({ email: p.email, naam: p.naam, maand: m })} /></td>
                  ))}
                  <td><Cel c={p.totaal} /></td>
                </tr>
              ))}
              <tr>
                <td><Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 13.5 }}>Bureau</Typography></td>
                {maanden.map((m) => <td key={m}><Cel c={data.bureau[m]} /></td>)}
                <td><Cel c={data.totaal} /></td>
              </tr>
            </tbody>
          </Box>
        </Box>
      )}

      <CelPaneel keuze={keuze} magBewerken={magBewerken} onClose={() => setKeuze(null)} />
      <SoortgroepenDialoog open={groepenOpen} magBewerken={magBewerken} onClose={() => setGroepenOpen(false)} onOpgeslagen={laad} />
    </Box>
  );
}
